/* Nikuya cart F0: pure decision/planning core. Does not set Shopify prices or mutate a cart. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NikuyaCartResolver = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const quantity = value => Number.isSafeInteger(value) && value >= 0;
  const text = value => typeof value === 'string' && value.length > 0 && value.length <= 160;
  const id = value => (typeof value === 'string' && value.length > 0) ||
    (Number.isSafeInteger(value) && value > 0);
  const role = (product, name) => Array.isArray(product.roles) && product.roles.includes(name);
  const managedTag = 'nikuya-acquisition-case-v1';

  function normalize(cart, catalog) {
    const errors = [];
    const variants = new Map();
    const products = new Map();
    if (!Array.isArray(catalog)) return { errors: ['CATALOG_UNCONFIGURED'], lines: [], products };
    for (const product of catalog) {
      if (!product || !text(product.key) || !id(product.productId) || !id(product.variantId) ||
          !Array.isArray(product.roles) || variants.has(String(product.variantId)) || products.has(product.key)) {
        errors.push('INVALID_OR_DUPLICATE_MAPPING'); continue;
      }
      variants.set(String(product.variantId), product);
      products.set(product.key, product);
    }
    if (!cart || !Array.isArray(cart.items)) errors.push('INVALID_CART');
    const keys = new Set();
    const lines = [];
    for (const line of (cart && Array.isArray(cart.items) ? cart.items : [])) {
      if (!line || !text(line.key) || keys.has(line.key) || !quantity(line.quantity) || line.quantity === 0) {
        errors.push('INVALID_CART_LINE'); continue;
      }
      keys.add(line.key);
      const product = variants.get(String(line.variant_id));
      if (!product || String(product.productId) !== String(line.product_id)) {
        errors.push('UNKNOWN_PRODUCT_MAPPING'); continue;
      }
      // F0 supports a mapped fixed-bundle parent only; never guess expanded parent/child ownership.
      if (line.parent_relationship || (Array.isArray(line.item_components) && line.item_components.length)) {
        errors.push('BUNDLE_REPRESENTATION_UNVERIFIED'); continue;
      }
      if (role(product, 'bundle') && (!Array.isArray(product.components) ||
          product.components.some(component => !text(component.key) || !quantity(component.quantity) ||
            component.quantity === 0 || !products.has(component.key) || role(products.get(component.key), 'bundle')))) {
        errors.push('BUNDLE_MAPPING_INVALID'); continue;
      }
      lines.push({ key: line.key, quantity: line.quantity, product,
        managed: role(product, 'case') && !!line.properties && line.properties._nikuya_reward === managedTag });
    }
    return { errors: [...new Set(errors)], lines, products };
  }

  /** Inputs are adapter snapshots. Readiness flags are gates, not financial credentials. */
  function resolve({ cart, catalog, acquisition = {}, suppressAcquisition = false,
    program = 'EVERGREEN', gates = {}, declinedCaseKeys = [] } = {}) {
    const state = normalize(cart, catalog);
    const { lines, products } = state;
    const owned = new Set();
    let knifeUnits = 0, heroUnits = 0, campaignUnits = 0;
    let completePresent = false;
    for (const line of lines) {
      const p = line.product;
      owned.add(p.key);
      if (role(p, 'bundle')) {
        completePresent = completePresent || p.bundleFamily === 'complete';
        for (const component of p.components) {
          owned.add(component.key);
          if (role(products.get(component.key), 'knife')) knifeUnits += component.quantity * line.quantity;
        }
      } else {
        if (role(p, 'knife')) knifeUnits += line.quantity;
        if (role(p, 'hero')) heroUnits += line.quantity;
        if (role(p, 'campaign_core') && !role(p, 'case') && !role(p, 'clearance')) campaignUnits += line.quantity;
      }
    }
    if (![knifeUnits, heroUnits, campaignUnits].every(quantity)) state.errors.push('QUANTITY_OVERFLOW');
    const composition = state.errors.length ? 'UNRESOLVED' :
      !lines.length ? 'EMPTY' : knifeUnits >= 2 ? 'MULTI_KNIFE' :
      knifeUnits === 1 && heroUnits === 1 ? 'ONE_HERO' : 'UNSUPPORTED';
    const suppressed = state.errors.map(reason => ({ reason }));
    const programReady = program === 'EVERGREEN';
    const acq = { visible: false, reason: 'NO_QUALIFYING_ENTRY', offerId: acquisition.offerId || null };
    if (acquisition.active === true && heroUnits > 0) {
      acq.reason = suppressAcquisition === true ? 'RECOGNIZED_PURCHASER' :
        state.errors.length ? 'MAPPING_UNVERIFIED' : !programReady ? 'PROGRAM_NOT_SUPPORTED_IN_F0' :
        gates.acquisition !== true ? 'ACQUISITION_ENFORCEMENT_UNVERIFIED' : 'QUALIFYING_ENTRY';
      acq.visible = acq.reason === 'QUALIFYING_ENTRY';
    }
    // Actual money is passthrough only: no local subtraction or second percentage application.
    const actualSubtotal = cart && quantity(cart.total_price) ? cart.total_price : null;
    const recommendations = [];
    const ranked = composition === 'ONE_HERO' ? [['kenma', 10], ['mori', 20]] :
      composition === 'MULTI_KNIFE' ? [['nagare', 20], ['kenma', null]] : [];
    const seenProducts = new Set();
    for (const [key, percent] of ranked) {
      const p = products.get(key);
      let reason = owned.has(key) ? 'ALREADY_OWNED_IN_CART' : !p ? 'UNKNOWN_PRODUCT_MAPPING' :
        lines.some(line => String(line.product.productId) === String(p.productId)) ? 'ALREADY_IN_CART' :
        seenProducts.has(String(p.productId)) ? 'DUPLICATE_PRODUCT' :
        p.available !== true ? 'OUT_OF_STOCK' : percent === null ? 'INCENTIVE_UNRESOLVED' :
        !programReady ? 'CAMPAIGN_ATTACH_PRECEDENCE_UNRESOLVED' :
        completePresent && gates.completeStorage !== true ? 'COMPLETE_APPLICABILITY_UNVERIFIED' :
        gates.attach !== true ? 'ATTACH_ENFORCEMENT_UNVERIFIED' : null;
      if (reason) { suppressed.push({ key, reason }); continue; }
      seenProducts.add(String(p.productId));
      recommendations.push({ key, productId: p.productId, variantId: p.variantId,
        percent, priceStatus: 'CONDITIONAL_OFFER', title: p.title || key });
      if (recommendations.length === 2) break;
    }
    const caseCandidates = new Map();
    if (acq.visible) {
      for (const line of lines.filter(item => role(item.product, 'hero') && !role(item.product, 'bundle'))) {
        const caseKey = line.product.caseKey;
        const p = products.get(caseKey);
        if (!p || !role(p, 'case')) { suppressed.push({ key: caseKey, reason: 'CASE_MAPPING_UNVERIFIED' }); continue; }
        if (p.available !== true) { suppressed.push({ key: caseKey, reason: 'CASE_UNAVAILABLE' }); continue; }
        if (Array.isArray(declinedCaseKeys) && declinedCaseKeys.includes(caseKey)) continue;
        caseCandidates.set(caseKey, { key: caseKey, variantId: p.variantId,
          quantity: (caseCandidates.get(caseKey)?.quantity || 0) + line.quantity });
      }
    }
    const desiredCases = [...caseCandidates.values()];
    const giftPlanningReady = gates.autoGift === true && gates.acquisition === true &&
      programReady && state.errors.length === 0 &&
      !suppressed.some(item => item.reason === 'CASE_MAPPING_UNVERIFIED');
    return { version: 1, composition, heroUnits, knifeUnits, campaignUnits,
      acquisition: acq, recommendations, suppressed, desiredCases, giftPlanningReady,
      actualSubtotal, actualCurrency: cart?.currency || null,
      financialAuthority: 'SHOPIFY', phase: 'F0_NOT_STOREFRONT_ACTIVATED' };
  }

  /** A dry-run plan only. Manual paid lines are never selected for change/removal. */
  function planCases(cart, catalog, decision) {
    const state = normalize(cart, catalog);
    if (state.errors.length || !decision || decision.giftPlanningReady !== true) {
      return { status: 'BLOCKED', reason: 'GIFT_PLANNING_NOT_READY', operations: [] };
    }
    const caseKeys = new Set(catalog.filter(p => role(p, 'hero')).map(p => p.caseKey));
    const desires = new Map();
    for (const desired of decision.desiredCases || []) {
      const p = state.products.get(desired.key);
      if (!p || !caseKeys.has(desired.key) || !role(p, 'case') || !quantity(desired.quantity) ||
          String(p.variantId) !== String(desired.variantId) || desires.has(desired.key)) {
        return { status: 'BLOCKED', reason: 'INVALID_GIFT_PLAN', operations: [] };
      }
      desires.set(desired.key, desired.quantity);
    }
    const operations = [];
    for (const caseKey of caseKeys) {
      const p = state.products.get(caseKey);
      if (!p || !role(p, 'case')) continue;
      const matching = state.lines.filter(line => line.product.key === caseKey);
      const manual = matching.filter(line => !line.managed).reduce((sum, line) => sum + line.quantity, 0);
      let remaining = Math.max(0, (desires.get(caseKey) || 0) - manual);
      for (const line of matching.filter(line => line.managed)) {
        const keep = Math.min(line.quantity, remaining);
        remaining -= keep;
        if (keep !== line.quantity) operations.push({ type: 'change', key: line.key, quantity: keep });
      }
      if (remaining > 0) operations.push({ type: 'add', variantId: p.variantId, quantity: remaining,
        properties: { _nikuya_reward: managedTag } });
    }
    return { status: 'PLANNED_NOT_EXECUTED', operations };
  }
  // F1B.2A contract only: consumes resolved authority; never restores context or writes a cart.
  const exactCasePairs = Object.freeze([
    Object.freeze(['NKY000052', 'NKY000101']),
    Object.freeze(['NKY000051', 'NKY000100']),
    Object.freeze(['NKY000059', 'NKY000129'])
  ]);
  const caseOwnershipVersion = 'nikuya-acquisition-case-v2';
  const caseMutationGates = Object.freeze(['caseReconciliationEnabled',
    'financialEnforcementVerified', 'casePricingVerified', 'exactMappingVerified',
    'exactCaseAvailable', 'acquisitionAuthorityValid']);
  const exactId = value => (typeof value === 'string' && /^[1-9]\d*$/.test(value)) ||
    (Number.isSafeInteger(value) && value > 0);
  function caseOwnershipProperties(mapping, revision) {
    return { _nikuya_reward: caseOwnershipVersion, _nikuya_hero_sku: mapping.heroSku,
      _nikuya_case_sku: mapping.caseSku, _nikuya_acq_revision: revision.acquisition,
      _nikuya_mapping_revision: revision.mapping };
  }

  // Classification orders owned excess only; it never establishes ownership or computes applied prices.
  function caseLineFinancialState(line, stage) {
    const code = stage === 'C4' ? 'NKYACQ15' : ['C1', 'C2', 'C3'].includes(stage) ? 'NKYACQ10' : null;
    const positive = n => Number.isSafeInteger(n) && n > 0;
    const amounts = ['original_price', 'original_line_price', 'final_price', 'final_line_price'];
    if (line.final_price === 0 || line.final_line_price === 0) return 'ZERO_UNSAFE';
    if (amounts.every(k => positive(line[k])) && line.final_price === line.original_price &&
        line.final_line_price === line.original_line_price) return 'FULL_PRICE_UNSAFE';
    const allocations = line.line_level_discount_allocations, allocation = allocations?.[0], app = allocation?.discount_application;
    const value = app?.value;
    const decimal = (typeof value === 'number' && Number.isFinite(value) && value > 0) ||
      (typeof value === 'string' && /^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value) && Number.isFinite(Number(value)) && Number(value) > 0);
    if (code && positive(line.quantity) && amounts.every(k => positive(line[k])) &&
        positive(line.original_price * line.quantity) && line.original_line_price === line.original_price * line.quantity &&
        line.final_price === 1 && line.final_line_price === line.quantity && line.original_price > 1 &&
        Array.isArray(allocations) && allocations.length === 1 && positive(allocation?.amount) &&
        allocation.amount === line.original_line_price - line.final_line_price && app?.title === code &&
        (app.code == null || app.code === code) && (app.type == null || app.type === 'discount_code') &&
        (app.target_type == null || app.target_type === 'line_item') && app.value_type === 'fixed_amount' && decimal &&
        (line.line_level_total_discount == null || line.line_level_total_discount === allocation.amount) &&
        (line.discounts == null || (Array.isArray(line.discounts) &&
          line.discounts.every(d => d && d.title === code && positive(d.amount)) &&
          (!line.discounts.length || line.discounts.reduce((n, d) => n + d.amount, 0) === allocation.amount)))) return 'VERIFIED';
    return 'AMBIGUOUS';
  }
  const caseCleanupGates = Object.freeze(['caseReconciliationEnabled', 'exactMappingVerified', 'acquisitionAuthorityValid']);

  function planCaseReconciliation(input = {}) {
    const { cart = {}, acquisition = {}, mappings = [], availability = {}, gates = {}, revision = {} } = input || {};
    const result = { version: 2, phase: 'F1B2A_CONTRACT_ONLY', status: 'BLOCKED',
      snapshotRevision: cart?.revision || null, cases: [], actions: [], blockers: [],
      diagnostics: [], executableActions: [], actualSubtotal: null, financialAuthority: 'SHOPIFY' };
    const fail = code => { result.cases = []; result.actions = []; result.blockers.push({ code }); return result; };
    if (!cart || cart.authoritative !== true || !text(cart.revision) || !Array.isArray(cart.items)) {
      return fail('AUTHORITATIVE_CART_REQUIRED');
    }
    if (!revision || !text(revision.mapping) || !text(revision.acquisition)) return fail('REVISION_REQUIRED');
    if (!Array.isArray(mappings) || mappings.length !== exactCasePairs.length) return fail('EXACT_MAPPING_UNVERIFIED');
    const variants = new Set(), products = new Set();
    for (const [heroSku, caseSku] of exactCasePairs) {
      const matches = mappings.filter(m => m && m.heroSku === heroSku && m.caseSku === caseSku);
      if (matches.length !== 1) return fail('EXACT_MAPPING_UNVERIFIED');
      const m = matches[0];
      for (const kind of ['hero', 'case']) {
        const variant = m[kind + 'VariantId'], product = m[kind + 'ProductId'];
        if (!exactId(variant) || !exactId(product) || variants.has(String(variant)) || products.has(String(product))) {
          return fail('EXACT_MAPPING_CONFLICT');
        }
        variants.add(String(variant)); products.add(String(product));
      }
    }
    const keys = new Set();
    for (const line of cart.items) {
      if (!line || !text(line.key) || keys.has(line.key) || !quantity(line.quantity) || !line.quantity ||
          !exactId(line.variant_id) || !exactId(line.product_id) || !text(line.sku) ||
          (line.properties != null && (typeof line.properties !== 'object' || Array.isArray(line.properties)))) {
        return fail('INVALID_CART_LINE');
      }
      keys.add(line.key);
      // An identity claim must agree on SKU, product and variant; titles never establish identity.
      for (const m of mappings) for (const kind of ['hero', 'case']) {
        const sku = m[kind + 'Sku'], variant = String(m[kind + 'VariantId']), product = String(m[kind + 'ProductId']);
        if ((line.sku === sku || String(line.variant_id) === variant) &&
            !(line.sku === sku && String(line.variant_id) === variant && String(line.product_id) === product)) {
          return fail('CART_MAPPING_IDENTITY_CONFLICT');
        }
      }
    }
    if (quantity(cart.total_price)) result.actualSubtotal = cart.total_price;
    // The existing context/purchaser resolver supplies these assertions, including TTL/revision validity.
    // A purchaser lookup failure is represented as NO_OVERRIDE, never positive purchase evidence.
    const authorityValid = !!acquisition && acquisition.status === 'VALID' &&
      acquisition.authorityVerified === true && acquisition.revisionStatus === 'ACTIVE' &&
      acquisition.revision === revision.acquisition && acquisition.expired === false &&
      acquisition.suppression === 'NO_OVERRIDE' && ['C1', 'C2', 'C3', 'C4'].includes(acquisition.stage);
    if (!authorityValid) result.blockers.push({ code: 'ACQUISITION_AUTHORITY_INVALID' });
    const same = (line, m, kind) => line.sku === m[kind + 'Sku'] &&
      String(line.variant_id) === String(m[kind + 'VariantId']) && String(line.product_id) === String(m[kind + 'ProductId']);
    for (const [heroSku, caseSku] of exactCasePairs) {
      const m = mappings.find(mapping => mapping.heroSku === heroSku);
      const properties = caseOwnershipProperties(m, revision);
      const matching = cart.items.filter(line => same(line, m, 'case')).sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
      const managed = [], manual = [], protectedLines = [];
      for (const line of matching) {
        const p = line.properties || {};
        const owned = Object.keys(properties).every(key => p[key] === properties[key]) &&
          Object.keys(p).length === Object.keys(properties).length && line.ownershipVerified === true &&
          !line.selling_plan_allocation && !line.parent_relationship && !(line.item_components?.length);
        // Private properties are customer writable. Independent ownership evidence is required.
        if (owned) managed.push(line);
        else if (Object.keys(p).some(key => key.startsWith('_nikuya_')) || line.ownershipVerified === true) protectedLines.push(line);
        else manual.push(line);
      }
      const eligible = cart.items.filter(line => same(line, m, 'hero') &&
        !line.parent_relationship && !(line.item_components?.length) && !line.selling_plan_allocation);
      const sum = lines => lines.reduce((n, line) => n + line.quantity, 0);
      const heroQuantity = sum(eligible), managedQuantity = sum(managed), manualQuantity = sum(manual);
      if (![heroQuantity, managedQuantity, manualQuantity, sum(protectedLines)].every(quantity)) return fail('QUANTITY_OVERFLOW');
      const entitlement = authorityValid ? heroQuantity : 0;
      const state = { heroSku, caseSku, eligibleHeroQuantity: heroQuantity, entitledQuantity: entitlement,
        currentManagedQuantity: managedQuantity, manualQuantity, protectedQuantity: sum(protectedLines),
        desiredManagedQuantity: entitlement, availability: availability?.[caseSku] || 'UNKNOWN',
        managedLines: managed.map(line => ({ key: line.key, quantity: line.quantity, financialState: caseLineFinancialState(line, acquisition.stage) })),
        excessQuantity: Math.max(0, managedQuantity - entitlement),
        managedKeys: managed.map(line => line.key), manualKeys: manual.map(line => line.key),
        protectedKeys: protectedLines.map(line => line.key), status: 'NO_OP', actions: [], blockers: [] };
      const block = code => state.blockers.push({ code, caseSku });
      if (protectedLines.length) block('OWNERSHIP_UNVERIFIED');
      if (manualQuantity && entitlement) {
        state.desiredManagedQuantity = null;
        block('MANUAL_CASE_FINANCIAL_RECONCILIATION_REQUIRED');
      }
      if (!state.blockers.length) {
        if (managedQuantity > entitlement) {
          let excess = managedQuantity - entitlement;
          // Unsafe first; stable fresh-key tie-break, independent of cart display order.
          const ordered = [...managed].sort((a, b) =>
            Number(caseLineFinancialState(a, acquisition.stage) === 'VERIFIED') -
            Number(caseLineFinancialState(b, acquisition.stage) === 'VERIFIED') || (a.key < b.key ? -1 : 1));
          for (const line of ordered) {
            if (!excess) break;
            const remove = Math.min(excess, line.quantity); excess -= remove;
            state.actions.push({ type: remove === line.quantity ? 'REMOVE_MANAGED_CASE' : 'DECREASE_MANAGED_CASE',
              caseSku, key: line.key, variantId: String(m.caseVariantId), quantity: line.quantity - remove });
          }
        } else if (managedQuantity < entitlement) {
          const remaining = entitlement - managedQuantity;
          if (managed.length) {
            const line = managed[0];
            state.actions.push({ type: 'INCREASE_MANAGED_CASE', caseSku, key: line.key,
              variantId: String(m.caseVariantId), quantity: line.quantity + remaining });
          } else state.actions.push({ type: 'ADD_MANAGED_CASE', caseSku, variantId: String(m.caseVariantId), quantity: remaining, properties });
        } else if (managed.some(line => caseLineFinancialState(line, acquisition.stage) !== 'VERIFIED')) {
          block('RETAINED_MANAGED_FINANCIAL_STATE_UNSAFE');
        }
      }
      const cleanup = state.actions.length > 0 && state.actions.every(a => ['DECREASE_MANAGED_CASE', 'REMOVE_MANAGED_CASE'].includes(a.type));
      state.cleanupOnly = cleanup;
      if (entitlement || managedQuantity) {
        if (!cleanup && state.availability !== 'AVAILABLE') {
          block(state.availability === 'UNAVAILABLE' ? 'BLOCKED_UNAVAILABLE' : 'BLOCKED_AVAILABILITY_UNKNOWN');
          state.actions = [];
        }
        for (const gate of cleanup ? caseCleanupGates : caseMutationGates) {
          if (gates?.[gate] !== true) block('GATE_CLOSED:' + gate);
        }
        if (!authorityValid) block('ACQUISITION_AUTHORITY_INVALID');
      }
      if ((managedQuantity || protectedLines.length) && (protectedLines.length || !authorityValid || gates?.casePricingVerified !== true || gates?.financialEnforcementVerified !== true)) {
        result.diagnostics.push({ code: 'STALE_MANAGED_CASE_REQUIRES_RECONCILIATION', caseSku,
          checkoutSafety: 'NOT_ESTABLISHED_IN_F1B2A' });
      }
      state.status = state.blockers.length ? 'BLOCKED' : state.actions.length ? 'READY' : 'NO_OP';
      if (!state.actions.length && !state.blockers.length) state.actions.push({ type: 'NO_OP', caseSku });
      result.cases.push(state);
      result.actions.push(...state.actions);
      result.blockers.push(...state.blockers);
    }
    result.status = result.blockers.length ? 'BLOCKED' : result.actions.some(a => a.type !== 'NO_OP') ? 'READY' : 'NO_OP';
    result.diagnostics.push({ code: 'REAL_EXECUTION_DISABLED_F1B2A' });
    return result;
  }
  return Object.freeze({ normalize, resolve, planCases, managedTag, planCaseReconciliation,
    caseOwnershipProperties, caseOwnershipVersion, caseMutationGates, caseCleanupGates, caseLineFinancialState });
});
